import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock, Info, Sparkles, Trash2 } from 'lucide-react';
import FieldInfoTip from '../ui/FieldInfoTip.jsx';
import {
  buildPlagesFromCommercantHoraires,
  countSlotsInPlage,
  formatPlageLabel,
  resolvePasReservationMinutes,
  summarizeCommercantHoraires,
} from '../../utils/prestationDisponibilite.js';

const JOUR_OPTIONS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 0, label: 'Dim' },
];

const DUREE_CRENEAU_PRESETS = [15, 20, 30, 45, 60, 75, 90, 120];
const PAS_DEPART_PRESETS = [15, 20, 30, 45, 60, 90, 120];

export default function PrestationDisponibiliteSection({
  typePrestation = 'RENDEZ_VOUS',
  commercant,
  dureeMinutes,
  maxParticipants = 1,
  editingPrestation,
  creneaux = [],
  creneauxLoading,
  creneauxError,
  creneauxFeedback,
  savingBulk,
  onBulkApplyPlages,
  onCopyFromBoutiqueHoraires,
  onClearAllPlages,
  onEditHorairesBoutique,
  onDeleteCreneau,
  onDureeMinutesChange,
}) {
  const isRdv = typePrestation === 'RENDEZ_VOUS';
  const isIndividuel = maxParticipants <= 1;
  const pasAuto = resolvePasReservationMinutes(commercant, { dureeMinutes, maxParticipants });
  const horairesResume = useMemo(
    () => summarizeCommercantHoraires(commercant?.horaires),
    [commercant?.horaires],
  );
  const usesCustomPlages = creneaux.length > 0;
  const [showCustom, setShowCustom] = useState(usesCustomPlages);
  const [bulkJours, setBulkJours] = useState([1, 2, 3, 4, 5]);
  const [bulkDebut, setBulkDebut] = useState('09:00');
  const [bulkFin, setBulkFin] = useState('18:00');
  const [bulkDuree, setBulkDuree] = useState(dureeMinutes || 60);
  const [bulkPas, setBulkPas] = useState(pasAuto);
  const [pasManuel, setPasManuel] = useState(false);

  useEffect(() => {
    setBulkDuree(dureeMinutes || 60);
  }, [dureeMinutes]);

  useEffect(() => {
    if (!pasManuel) {
      setBulkPas(resolvePasReservationMinutes(commercant, { dureeMinutes: bulkDuree, maxParticipants }));
    }
  }, [commercant, bulkDuree, maxParticipants, pasManuel]);

  const slotsPreview = countSlotsInPlage(bulkDebut, bulkFin, bulkPas, bulkDuree);

  const handleDureeChange = (nextDuree) => {
    const value = Math.max(15, parseInt(nextDuree, 10) || 15);
    setBulkDuree(value);
    onDureeMinutesChange?.(value);
    if (isRdv && isIndividuel) {
      setPasManuel(false);
    }
  };

  const handlePasChange = (nextPas) => {
    const value = Math.max(15, parseInt(nextPas, 10) || 15);
    setBulkPas(value);
    setPasManuel(true);
  };

  const toggleJour = (jour) => {
    setBulkJours((prev) =>
      prev.includes(jour) ? prev.filter((j) => j !== jour) : [...prev, jour].sort((a, b) => a - b),
    );
  };

  if (!editingPrestation) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">Disponibilité automatique (recommandé)</p>
            <p className="mt-1 text-blue-800">
              Enregistrez d&apos;abord cette activité. Sans plage personnalisée, les clients pourront réserver
              pendant vos <strong>horaires d&apos;ouverture</strong>, avec un créneau toutes les{' '}
              <strong>{pasAuto} min</strong> (durée de la prestation : {dureeMinutes || 60} min).
            </p>
            {horairesResume.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-blue-800">
                {horairesResume.map((row) => (
                  <li key={row.jour}>
                    <strong>{row.label}</strong> : {row.plages.join(', ')}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-amber-800">
                Aucun horaire boutique configuré — renseignez-les dans les paramètres du commerçant.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {creneauxError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {creneauxError}
        </div>
      )}
      {creneauxFeedback && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {creneauxFeedback}
        </div>
      )}

      {!usesCustomPlages && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-emerald-900">
                Mode automatique — horaires de la boutique
              </p>
              <p className="mt-1 text-emerald-800">
                {isRdv
                  ? `Les rendez-vous sont proposés toutes les ${pasAuto} min pendant vos heures d'ouverture.`
                  : 'Les créneaux sont générés automatiquement dans vos plages d\'ouverture.'}
                {' '}Ex. prestation {dureeMinutes || 20} min → départs toutes les {pasAuto} min (9h00, 9h20, 9h40…).
              </p>
              {horairesResume.length > 0 ? (
                <ul className="mt-3 space-y-1 text-xs text-emerald-900">
                  {horairesResume.map((row) => (
                    <li key={row.jour} className="flex gap-2">
                      <span className="w-20 shrink-0 font-medium">{row.label}</span>
                      <span>{row.plages.join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-amber-800">Configurez les horaires d&apos;ouverture de la boutique.</p>
              )}
              {onEditHorairesBoutique && (
                <button
                  type="button"
                  onClick={onEditHorairesBoutique}
                  className="mt-3 text-xs font-medium text-emerald-700 underline hover:text-emerald-900"
                >
                  Modifier les horaires de la boutique
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {usesCustomPlages && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Plages personnalisées actives</strong> — elles remplacent les horaires boutique pour cette
          activité. Chaque plage est découpée automatiquement (pas de {bulkPas} min).
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!showCustom && !usesCustomPlages && (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            Définir des plages spécifiques à cette activité
          </button>
        )}
        {usesCustomPlages && onClearAllPlages && (
          <button
            type="button"
            onClick={onClearAllPlages}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Trash2 className="h-4 w-4" />
            Revenir aux horaires boutique
          </button>
        )}
      </div>

      {(showCustom || usesCustomPlages) && (
        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Ajout rapide de plages</h4>
            <FieldInfoTip label="Plages horaires">
              Une plage = une fenêtre (ex. Lundi 9h–18h). Indiquez aussi la durée d&apos;un créneau et
              l&apos;intervalle entre les départs : le système calcule automatiquement les horaires
              réservables proposés aux clients.
            </FieldInfoTip>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-gray-600">Jours concernés</p>
            <div className="flex flex-wrap gap-2">
              {JOUR_OPTIONS.map((j) => (
                <button
                  key={j.value}
                  type="button"
                  onClick={() => toggleJour(j.value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border ${
                    bulkJours.includes(j.value)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {j.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-gray-600">Durée d&apos;un créneau (min)</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DUREE_CRENEAU_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handleDureeChange(preset)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                      bulkDuree === preset
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={15}
                step={5}
                value={bulkDuree}
                onChange={(e) => handleDureeChange(e.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-gray-600">
                Intervalle entre départs (min)
                <FieldInfoTip label="Intervalle entre départs">
                  {isRdv && isIndividuel
                    ? 'Pour un rendez-vous individuel, laissez la même valeur que la durée (ex. 20 min → 9h00, 9h20, 9h40).'
                    : 'Ex. terrain 60 min avec départs toutes les 15 min si plusieurs créneaux peuvent se chevaucher.'}
                </FieldInfoTip>
              </label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {PAS_DEPART_PRESETS.filter((p) => p >= 15).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePasChange(preset)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                      bulkPas === preset
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={15}
                step={5}
                value={bulkPas}
                onChange={(e) => handlePasChange(e.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              />
              {isRdv && isIndividuel && bulkPas !== bulkDuree && (
                <button
                  type="button"
                  onClick={() => {
                    setPasManuel(false);
                    setBulkPas(bulkDuree);
                  }}
                  className="mt-1 text-xs text-indigo-600 underline hover:text-indigo-800"
                >
                  Aligner sur la durée ({bulkDuree} min)
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Début</label>
              <input
                type="time"
                value={bulkDebut}
                onChange={(e) => setBulkDebut(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Fin</label>
              <input
                type="time"
                value={bulkFin}
                onChange={(e) => setBulkFin(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              />
            </div>
          </div>

          <p className="text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
            <Clock className="inline h-3.5 w-3.5 mr-1" />
            Aperçu : environ <strong>{slotsPreview}</strong> créneau{slotsPreview > 1 ? 'x' : ''} par jour
            sélectionné (durée <strong>{bulkDuree} min</strong>, départs toutes les <strong>{bulkPas} min</strong>).
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={savingBulk || bulkJours.length === 0}
              onClick={() =>
                onBulkApplyPlages?.({
                  jours: bulkJours,
                  heureDebut: bulkDebut,
                  heureFin: bulkFin,
                  dureeMinutes: bulkDuree,
                  pasMinutes: bulkPas,
                  replace: usesCustomPlages,
                })
              }
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingBulk ? 'Enregistrement…' : 'Appliquer ces plages'}
            </button>
            {onCopyFromBoutiqueHoraires && horairesResume.length > 0 && (
              <button
                type="button"
                disabled={savingBulk}
                onClick={onCopyFromBoutiqueHoraires}
                className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                Copier les horaires boutique
              </button>
            )}
          </div>
        </div>
      )}

      {usesCustomPlages && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-indigo-500" />
            Plages configurées
          </h4>
          {creneauxLoading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : (
            <ul className="space-y-2">
              {creneaux.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  <span>{formatPlageLabel(c)}</span>
                  <button
                    type="button"
                    onClick={() => onDeleteCreneau?.(c)}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Supprimer
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
